/**
 * PageLockIndicator
 *
 * Presentational lock icon for a TOC page row's actions column. Renders one of three
 * MUI icons based on the page's lock state, colored via the MUI `color` prop so colors
 * resolve from the active GFT theme and adapt to light/dark mode (no hardcoded hex, no `sx`):
 *
 * - UNLOCKED         → LockOpenIcon   (color="action",  muted/gray)
 * - LOCKED_BY_OTHER  → LockIcon       (color="warning", orange)
 * - LOCKED_BY_SELF   → LockPersonIcon (color="primary", blue)
 *
 * State is derived by comparing the lock holder (`lockedBy`) against the current session
 * user (`username` from UserContext). Each state carries a distinct glyph and an
 * `aria-label`, so the state is never conveyed by color alone.
 *
 * @module PageLockIndicator
 */
import './PageLockIndicator.scss';

import { useUserContext } from '@context/UserContext';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockPersonIcon from '@mui/icons-material/LockPerson';
import { Tooltip } from '@mui/material';
import { formatTimestampToLocal } from '@utils/dateHelpers';
import { PageLockIndicatorProps, PageLockState } from 'types/TOC.types';

/**
 * Derives the lock state for a page from its lock projection and the current user.
 *
 * @param lockedBy - Alias of the lock holder, if any.
 * @param username - The current session user's alias.
 * @returns The resolved {@link PageLockState}.
 */
const resolveLockState = (lockedBy: string | undefined, username: string): PageLockState => {
  if (!lockedBy) {
    return PageLockState.UNLOCKED;
  }
  return lockedBy === username ? PageLockState.LOCKED_BY_SELF : PageLockState.LOCKED_BY_OTHER;
};

/**
 * Builds the tooltip title / aria-label for a lock state, appending the auto-release
 * time when a `lockExpiry` is present (formatted to match the locked-pages banner).
 *
 * @param state - The resolved lock state.
 * @param lockedBy - Alias of the lock holder, if any.
 * @param lockExpiry - ISO-8601 auto-release timestamp, if any.
 * @returns The human-readable label describing the lock.
 */
const buildLockLabel = (state: PageLockState, lockedBy: string | undefined, lockExpiry: string | undefined): string => {
  if (state === PageLockState.UNLOCKED) {
    return 'Not locked';
  }

  const base = state === PageLockState.LOCKED_BY_SELF ? 'Locked by you' : `Locked by ${lockedBy}`;
  const expiry = lockExpiry ? formatTimestampToLocal(lockExpiry, 'h:mm A') : '';
  return expiry ? `${base} — auto-releases at ${expiry}` : base;
};

/**
 * Renders the per-page lock icon for the TOC actions column.
 *
 * @param props - {@link PageLockIndicatorProps}
 * @returns The lock icon wrapped in a tooltip.
 */
const PageLockIndicator = ({ lock }: PageLockIndicatorProps) => {
  const { username } = useUserContext();
  const state = resolveLockState(lock?.lockedBy, username);
  const label = buildLockLabel(state, lock?.lockedBy, lock?.lockExpiry);

  // MUI SvgIcon renders aria-hidden by default, so a bare aria-label would be skipped by screen
  // readers. We do NOT use titleAccess here: it emits a native <title>, which the browser renders
  // as its own tooltip on top of the MUI <Tooltip> below (a visible double-tooltip). Instead we
  // expose the icon explicitly: role='img' (SvgIcon only sets this when titleAccess is passed, and
  // many screen readers ignore aria-label on a role-less <svg>) + aria-hidden={false} + aria-label,
  // so the lock state is announced with no second native tooltip.
  const iconProps = {
    className: 'page-lock-indicator',
    fontSize: 'small' as const,
    role: 'img',
    'aria-label': label,
    'aria-hidden': false,
  };

  const renderIcon = () => {
    switch (state) {
      case PageLockState.LOCKED_BY_SELF:
        return <LockPersonIcon color='primary' {...iconProps} />;
      case PageLockState.LOCKED_BY_OTHER:
        return <LockIcon color='warning' {...iconProps} />;
      case PageLockState.UNLOCKED:
      default:
        return <LockOpenIcon color='action' {...iconProps} />;
    }
  };

  return <Tooltip title={label}>{renderIcon()}</Tooltip>;
};

export default PageLockIndicator;
